<template>
    <main id="LmUsersNewOrEdit" class="wrapper-content pr-0">
        <LmComponentLoading v-if="isLoading" fixed />
        <b-row>
          <b-col>
            <h1 v-if="!isOnEditMode">Nuevo Usuario</h1>
            <h1 v-if="isOnEditMode">Editar Usuario</h1>
            <b-row>
              <LmTextInput
                class="col-6"
                @enter="onSubmit"
                :v="$v.form.firstname"
                v-model="form.firstname"
                label="Nombre"
              />
              <LmTextInput
                class="col-6"
                @enter="onSubmit"
                :v="$v.form.lastname"
                v-model="form.lastname"
                label="Apellido"
              />
              <LmEmailInput
                class="col-6"
                @enter="onSubmit"
                :v="$v.form.email"
                v-model="form.email"
                label="Email"
              />
              <LmTreeSelect
                class="col-6"
                :v="$v.form.role"
                label="Rol"
                pathToGetOptions="/customer/roles"
                v-model="form.role"
              />
              <LmTreeSelect
                class="col-6"
                label="Área"
                pathToGetOptions="/customer/areas"
                v-model="form.area"
              />
              <LmTreeSelect
                class="col-6"
                label="Subárea"
                pathToGetOptions="/customer/subareas"
                childrenOf="area"
                v-model="form.subarea"
              />
            </b-row>
            <hr>
            <!-- Action Buttons -->
            <div class="row">
              <LmButton
                  :value="$t('cancel')"
                  class="col-auto"
                  type="button"
                  buttonClass="btn-secondary"
                  @click="onCancel()"
              />
              <LmButton
                  :value="$t('save')"
                  class="col-auto"
                  type="button"
                  buttonClass="btn-primary"
                  @click="onSubmit()"
              />
              <LmButton
                  v-if="isOnEditMode && !form.deleted"
                  :value="$t('delete')"
                  class="col-auto"
                  action="delete"
                  buttonClass="btn-danger"
                  confirmationTitle="Vas a eliminar el usuario"
                  @click="onDelete()"
              />
              <LmButton
                  v-if="isOnEditMode && form.deleted"
                  value="Reactivar"
                  class="col-auto"
                  buttonClass="btn-success"
                  @click="onReactivate()"
              />
            </div>
          </b-col>
          <!-- Toolbar -->
          <LmToolbar
            ref="toolbar"
            :isOnEditMode="isOnEditMode"
            :items="toolbarItems"
            :config="toolbarConfig"
          />
        </b-row>
    </main>
</template>

<script>
// Components
import LmButton from '@/components/form/elements/LmButton'
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmEmailInput from '@/components/form/elements/LmEmailInput'
import LmForm from '@/components/form/LmForm'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import LmToolbar from '@/components/lmToolBar'

// Utils
import commonsApi from '@/api/commons'
import lmToasts from '@/mixins/lmToasts.js'
import { required, email } from 'vuelidate/lib/validators'

export default {
  provide () {
    return {
      context: this.form
    }
  },
  mixins: [lmToasts],
  components: {
    LmForm,
    LmButton,
    LmTextInput,
    LmEmailInput,
    LmTreeSelect,
    LmToolbar
  },
  props: {
    isOnEditMode: {
      type: Boolean,
      default: false
    },
    pathToRedirectOnCancel: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      deleted: false,
      toolbarMode: null,
      form: {
        area: null,
        subarea: null,
        role: null,
        firstname: '',
        lastname: '',
        email: '',
        deleted: false
      },
      isLoading: false
    }
  },
  validations: {
    form: {
      firstname: {
        required
      },
      lastname: {
        required
      },
      email: {
        required,
        email
      },
      role: {
        required
      }
    }
  },
  computed: {
    pathToGetOptionsForSubAreas () {
      if (this.form.area) {
        return `/customer/subareas/${this.form.area.id}`
      }
      return null
    },
    toolbarItems () {
      let items = []
      if (this.isOnEditMode) {
        items = [
          'logMovements',
          'auditMovements'
        ]
      }
      return items
    },
    toolbarConfig () {
      return {
        auditMovements: {
          serviceName: 'users'
        },
        logMovements: {}
      }
    }
  },
  methods: {
    async onSubmit () {
      try {
        this.isLoading = true

        // Validaciones
        this.$v.$touch()
        await this.validateForm(this.form)
        if (this.$v.$invalid) {
          return this.lmToastDangerMessage(this.$t('form_with_errors'), 'Error')
        }

        if (!this.isOnEditMode) {
          // Nuevo
          await commonsApi.axiosPostWrapper('/users/', this.form)
          this.lmToastSuccessMessage(`Se creó el usuario correctamente.`, 'Se guardó exitosamente')
        } else {
          // Editar
          await commonsApi.axiosPutWrapper(`/users/update/${this.$route?.params?.id}`, this.form)
          this.lmToastSuccessMessage(`Se editó el usuario correctamente.`, 'Se guardó exitosamente')
        }

        setTimeout(() => {
          this.$router.push(this.pathToRedirectOnCancel)
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    onCancel () {
      this.$router.push(this.pathToRedirectOnCancel)
    },
    validateForm (validation) {
      // Valido el email con https://emailregex.com/
      const emailregex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      if (!emailregex.test(validation.email)) {
        this.lmToastDangerMessage(this.$t('El email ingresado no es válido'), 'Error')
        throw new Error('validation error')
      }
    },
    async onDelete () {
      try {
        this.isLoading = true

        await commonsApi.axiosPutWrapper(`/users/delete/${this.$route?.params?.id}`)
        this.lmToastSuccessMessage(`Se eliminó el usuario correctamente.`, 'Se eliminó correctamente')

        setTimeout(() => {
          this.$router.push(this.pathToRedirectOnCancel)
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onReactivate () {
      try {
        this.isLoading = true

        await commonsApi.axiosPutWrapper(`/users/reactivate/${this.$route?.params?.id}`)
        this.lmToastSuccessMessage(`Se reactivó el usuario correctamente.`, 'Se reactivó correctamente')

        setTimeout(() => {
          this.$router.push(this.pathToRedirectOnCancel)
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    }
  },
  async mounted () {
    if (this.$route?.params?.id) {
      try {
        const user = await commonsApi.axiosGetWrapper(`/users/${this.$route?.params?.id}`)
        for (const prop in user.data) {
          if (this.form.hasOwnProperty(prop)) {
            this.form[prop] = user.data[prop]
          }
        }
      } catch (error) {
        console.log(error)
      }
    }
    // Creamos un watcher para limpiar la subarea cuando se cambia el area
    // Debe estar despues de que se cargan los values en modo edición
    // this.$nextTick(() => {
    //   this.$watch('form.area', _ => {
    //     this.form.subarea = null
    //   })
    // })
  }
}

</script>

<style>
.button {
  margin-top: 35px;
}
</style>
